import { QueryFunction, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { signOutOfPush } from '../library/pushSubscription';
import { parseApiError, tenantsFromSelectionError } from '../library/apiErrorHelper';
import { IJwtClaims, readJwtClaims } from '../library/jwtClaims';
import { ISession } from '../library/models/session';
import { apiService } from '../services/api';
import { useReactMutation, useReactQuery } from '../services/queryService';
import {
    endImpersonation,
    forgetImpersonation,
    isImpersonating,
    leaveImpersonation,
    onSessionEvent,
    SessionEvent,
    sessionStore,
} from '../services/sessionKeeper';
import { services } from '../services/services';
import { AuthResponse, AuthState, TenantSummary, User, UserLoginRequest, UserRegistrationRequest, UserRole } from '../types';

/**
 * Thrown by `login` when the account belongs to more than one clinic and the caller did not
 * say which. Not a failure: the credentials were accepted, and the only thing missing is the
 * choice. Callers are expected to catch this specifically, show the clinics it carries, and
 * call `login` again with the chosen `tenantId` -- the auth state stays signed-out and
 * error-free in the meantime, so no red alert appears.
 */
export class TenantSelectionRequiredError extends Error {
    readonly tenants: TenantSummary[];

    constructor(tenants: TenantSummary[]) {
        super('Clinic selection required.');
        this.name = 'TenantSelectionRequiredError';
        this.tenants = tenants;
    }
}

interface AuthContextType extends AuthState {
    /**
     * Resolves with the response rather than void, because the caller needs the role to
     * decide where to land. Reading it from this context instead would read the previous
     * value -- the dispatch below is queued, so it has not been applied yet when the
     * caller's `await` returns.
     *
     * Rejects with {@link TenantSelectionRequiredError} when the account belongs to several
     * clinics and `credentials.tenantId` was not set; every other failure is reported
     * through the context's `error` instead.
     */
    login: (credentials: UserLoginRequest) => Promise<AuthResponse>;
    /**
     * Takes up a session some other endpoint has already minted -- one that proved who the
     * caller is without `/auth/login`, such as following a one-off link and typing a password.
     * Exactly what a successful sign-in stores, so nothing downstream can tell the two apart.
     */
    adoptSession: (response: AuthResponse) => void;
    register: (userData: UserRegistrationRequest) => Promise<AuthResponse>;
    logout: () => void;
    updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
    | { type: 'LOGIN_START' }
    | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string; role: UserRole | null } }
    | { type: 'LOGIN_FAILURE'; payload?: string }
    | { type: 'LOGIN_TENANT_REQUIRED' }
    | { type: 'LOGOUT' }
    | { type: 'SESSION_ENDED' }
    | { type: 'TOKEN_RENEWED'; payload: string }
    | { type: 'UPDATE_USER'; payload: User }
    | { type: 'SESSION_REFRESHED'; payload: { user: User; role: UserRole | null } }
    | { type: 'CLEAR_ERROR' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
    switch (action.type) {
        case 'LOGIN_START':
            return {
                ...state,
                isLoading: true,
                error: undefined,
                hasFailed: false,
                sessionEnded: false,
            };
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload.user,
                token: action.payload.token,
                role: action.payload.role,
                isLoading: false,
                error: undefined,
                hasFailed: false,
                sessionEnded: false,
            };
        case 'LOGIN_FAILURE':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                token: null,
                role: null,
                isLoading: false,
                error: action.payload,
                hasFailed: true,
            };
        // Stops loading without recording an error: the sign-in has not failed, it is
        // waiting on the clinic choice. Clearing `error` also wipes any message left over
        // from an earlier attempt, so the picker is not shown under a stale red alert.
        case 'LOGIN_TENANT_REQUIRED':
            return {
                ...state,
                isLoading: false,
                error: undefined,
                hasFailed: false,
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                token: null,
                role: null,
                isLoading: false,
                error: undefined,
                hasFailed: false,
                sessionEnded: false,
            };
        // Signed out by the server rather than by the person: the token ran out, or stopped
        // being accepted. The same state as LOGOUT, plus the fact of it, so the sign-in page
        // they are about to land on can say why they are there.
        case 'SESSION_ENDED':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                token: null,
                role: null,
                isLoading: false,
                error: undefined,
                hasFailed: false,
                sessionEnded: true,
            };
        // The same session with a newer token. Like SESSION_REFRESHED, never applied to a
        // session that has since been signed out.
        case 'TOKEN_RENEWED':
            return state.isAuthenticated ? { ...state, token: action.payload } : state;
        case 'UPDATE_USER':
            return {
                ...state,
                user: action.payload,
            };
        // Only ever applied to a session that is still signed in: a refresh landing after a
        // sign-out must not resurrect it.
        case 'SESSION_REFRESHED':
            return state.isAuthenticated
                ? { ...state, user: action.payload.user, role: action.payload.role }
                : state;
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: undefined,
                hasFailed: false,
            };
        default:
            return state;
    }
};

const initialState: AuthState = {
    user: null,
    token: null,
    role: null,
    isAuthenticated: false,
    isLoading: true,
    error: undefined,
    hasFailed: false,
    sessionEnded: false,
};

/** Anything not in the union is treated as no role at all, including a hand-edited value. */
const readStoredRole = (): UserRole | null => {
    const stored = sessionStore().getItem('userRole');
    const roles: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'THERAPIST', 'CUSTOMER'];

    return roles.includes(stored as UserRole) ? (stored as UserRole) : null;
};

/**
 * The session sign-in stored, if there is a usable one: a token that has not expired and a
 * user that parses. Null otherwise -- the caller decides whether that means clearing it.
 */
const readStoredSession = (): { user: User; token: string; role: UserRole | null } | null => {
    const token = sessionStore().getItem('authToken');
    const userStr = sessionStore().getItem('user');

    if (!token || !userStr || !apiService.isAuthenticated()) {
        return null;
    }

    try {
        // Null role for anyone who signed in before roles were stored; they keep every
        // ordinary route and simply see no role-gated ones.
        return { user: JSON.parse(userStr) as User, token, role: readStoredRole() };
    } catch {
        return null;
    }
};

/**
 * Whether two tokens are one session: the same person at the same clinic. That is all a
 * renewal keeps, and all that telling a renewal from a sign-in as somebody else needs.
 */
const sameHolder = (current: string | null, next: string | null): boolean => {
    const a: IJwtClaims | null = readJwtClaims(current);
    const b: IJwtClaims | null = readJwtClaims(next);

    return !!a?.sub && a.sub === b?.sub && a.tid === b.tid;
};

/** Whether a token is past its own date. An unreadable one has not run out; it never ran. */
const hasRunOut = (token: string | null): boolean => {
    const expiresAt: number | undefined = readJwtClaims(token)?.exp;

    return typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now();
};

/** Same comparison for the user and the role: anything that would change what is drawn. */
const sameSession = (a: { user: User | null; role: UserRole | null }, b: ISession): boolean =>
    a.role === b.role && JSON.stringify(a.user) === JSON.stringify(b.user);

interface AuthProviderProps {
    children: ReactNode;
}

/**
 * The signed-in session, painted from localStorage and then kept honest by the server.
 *
 * <b>localStorage is the first paint, not the truth.</b> It used to be both: what sign-in
 * stored was what every avatar, name and role-gated route read for the whole life of the
 * token (a day), so a picture changed on a phone, a language changed in another tab or a
 * role an admin took away stayed invisible to this browser until the next sign-in -- and a
 * replaced picture's URL answers 404, so the avatar fell back to an initial. `GET /users/me`
 * now runs on boot and on every window focus (the query client's defaults), and whatever it
 * answers replaces the stored copy.
 *
 * Other tabs are followed through the `storage` event, which fires in every tab except the
 * one that wrote: an edit, a sign-out or a sign-in as somebody else there is reflected here
 * without waiting for focus.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const queryClient = useQueryClient();
    const loginMutation = useReactMutation<AuthResponse, Error, UserLoginRequest>(services.authService.loginMutation());
    const registerMutation = useReactMutation<AuthResponse, Error, UserRegistrationRequest>(services.authService.registerMutation());

    /**
     * The state as of the last write this provider made, not the last render. `updateUser`
     * merges onto it, so two edits in one tick -- or one made from a stale closure -- build
     * on each other instead of the second silently undoing the first.
     */
    const latest = useRef<AuthState>(state);
    latest.current = state;

    /**
     * Every change this tab learns of before the server can tell it -- its own optimistic
     * edit, or one another tab wrote -- is newer than any refresh already under way, and
     * that refresh must not put the old value back. Two ways it could:
     *
     * - still in flight: `localEdits` is bumped, and the query function hands back the local
     *   session instead of an answer to a question asked before the edit;
     * - already answered but not yet delivered to this component: too late for that, so the
     *   effect below drops any answer that is not newer than `lastLocalEditAt`.
     *
     * Cancelling the request covers neither of these reliably, which is why it is not used.
     * The next focus asks again, and by then the server knows too.
     */
    const localEdits = useRef<number>(0);
    const lastLocalEditAt = useRef<number>(0);
    const noteLocalEdit = (): void => {
        localEdits.current += 1;
        lastLocalEditAt.current = Date.now();
    };

    const meQuery = services.users.getMe();
    const meKey = meQuery.queryKey;
    const askServer = meQuery.queryFn as QueryFunction<ISession>;
    const guardedMeQuery: UseQueryOptions<ISession, Error> & { queryKey: readonly unknown[] } = {
        ...meQuery,
        queryFn: async (context) => {
            const sentAfter: number = localEdits.current;
            const answer: ISession = await askServer(context);
            const { user, role } = latest.current;

            return sentAfter === localEdits.current || !user
                ? answer
                : { user, role: role ?? answer.role };
        },
    };

    /**
     * Forgets the last answer. Called whenever the session changes hands, because the key
     * is the same for everyone: without this, the next account to sign in would be shown
     * the previous one's cached answer until its own request came back.
     */
    const forgetSession = useCallback(
        () => queryClient.removeQueries({ queryKey: meKey, exact: true }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [queryClient]
    );

    const hydrateFromStorage = useCallback((): boolean => {
        const stored = readStoredSession();

        if (stored) {
            dispatch({ type: 'LOGIN_SUCCESS', payload: stored });
            return true;
        }

        return false;
    }, []);

    useEffect(() => {
        dispatch({ type: 'LOGIN_START' });

        // An impersonation that ran out while the tab was away goes, and only it: what is
        // underneath is the administrator's own session, judged on its own terms below.
        if (isImpersonating() && !readStoredSession()) {
            forgetImpersonation();
        }

        if (!hydrateFromStorage()) {
            // Whatever is there is expired or unreadable; nothing to keep. And somebody who
            // comes back to a session that ran out while they were away was signed out by
            // the clock, which is worth saying -- unlike having no session at all.
            if (localStorage.getItem('authToken')) {
                services.authService.removeAuthToken();
                dispatch({ type: 'SESSION_ENDED' });
            } else {
                dispatch({ type: 'LOGOUT' });
            }
        }
    }, [hydrateFromStorage]);

    /**
     * What `sessionKeeper` does to the stored token, followed here. It works below React, in
     * the axios interceptors, so this is how its two outcomes reach the state:
     *
     * - `ended` is what makes a refused token sign the person out *now*. The page used to go
     *   on looking signed in, every request failing, until the next window focus asked
     *   `/users/me` and that 401 was finally noticed.
     * - `renewed` only swaps the token. Nothing drawn depends on which token it is.
     */
    useEffect(() => onSessionEvent((event: SessionEvent): void => {
        if (event.type === 'ended') {
            forgetSession();
            dispatch({ type: 'SESSION_ENDED' });
            return;
        }

        latest.current = { ...latest.current, token: event.token };
        dispatch({ type: 'TOKEN_RENEWED', payload: event.token });
    }), [forgetSession]);

    const { data: session, dataUpdatedAt, error: sessionError } = useReactQuery<ISession>(guardedMeQuery, {
        enabled: state.isAuthenticated,
        // A 401 is an answer, not a blip; anything else is simply asked again on next focus.
        retry: false,
    });

    useEffect(() => {
        if (!session || !latest.current.isAuthenticated || sameSession(latest.current, session)
            || dataUpdatedAt <= lastLocalEditAt.current) {
            return;
        }

        // Wherever this tab's session lives: written to localStorage while impersonating, the
        // therapist would surface in every other tab of the administrator's.
        sessionStore().setItem('user', JSON.stringify(session.user));
        sessionStore().setItem('userRole', session.role);
        dispatch({ type: 'SESSION_REFRESHED', payload: session });
    }, [session, dataUpdatedAt]);

    useEffect(() => {
        // `sessionKeeper` has already cleared storage and reported the 401 above; this says
        // the same thing from the query's side, and is all there is when the query is not
        // going through axios at all. Anything else -- offline, a 5xx -- keeps the session:
        // a flaky connection is no reason to sign anybody out.
        if ((sessionError as AxiosError | null)?.response?.status === 401) {
            forgetSession();
            dispatch({ type: 'SESSION_ENDED' });
        }
    }, [sessionError, forgetSession]);

    useEffect(() => {
        const onStorage = (event: StorageEvent): void => {
            if (event.storageArea !== localStorage) {
                return;
            }

            // localStorage is the administrator's own session, shadowed in this tab. Their
            // renewals and edits elsewhere change nothing here; their signing out, or signing
            // in as somebody else, ends the impersonation they started.
            if (isImpersonating()) {
                if (event.key === null || (event.key === 'authToken' && !sameHolder(event.oldValue, event.newValue))) {
                    leaveImpersonation('/');
                }
                return;
            }

            // A null key is `localStorage.clear()`, which signs out as surely as removing the token.
            if (event.key === null || (event.key === 'authToken' && event.newValue === null)) {
                forgetSession();
                // The other tab cannot say whether it signed out or was signed out, but the
                // token this one holds can: nobody chose to give up one that had run out.
                dispatch({ type: hasRunOut(latest.current.token) ? 'SESSION_ENDED' : 'LOGOUT' });
                return;
            }

            if (event.key === 'authToken' && event.newValue !== latest.current.token) {
                // The other tab renewed the session this one shares. Same person, same
                // clinic, so everything cached is still theirs -- and since this happens
                // about hourly, clearing it would blank every other open tab each time.
                if (latest.current.isAuthenticated && sameHolder(latest.current.token, event.newValue)) {
                    latest.current = { ...latest.current, token: event.newValue };
                    dispatch({ type: 'TOKEN_RENEWED', payload: event.newValue as string });
                    return;
                }

                // Somebody else, or another clinic: nothing cached belongs to this session.
                queryClient.clear();
                if (!hydrateFromStorage()) {
                    dispatch({ type: 'LOGOUT' });
                }
                return;
            }

            if ((event.key === 'user' || event.key === 'userRole') && latest.current.isAuthenticated) {
                const stored = readStoredSession();
                if (stored) {
                    noteLocalEdit();
                    latest.current = { ...latest.current, user: stored.user, role: stored.role };
                    dispatch({ type: 'SESSION_REFRESHED', payload: stored });
                }
            }
        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [queryClient, forgetSession, hydrateFromStorage]);

    const adoptSession = (response: AuthResponse): void => {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem('userRole', response.role);
        services.authService.setAuthToken(response.token);
        forgetSession();

        dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
                user: response.user,
                token: response.token,
                role: response.role,
            },
        });
    };

    /**
     * mutateAsync, not mutate: mutate returns void, so awaiting it resolved immediately
     * and the caller navigated before the outcome was known -- a failed sign-in still
     * redirected, and the error thrown from the onError callback never reached the
     * caller's catch because React Query invokes that callback itself.
     */
    const login = async (credentials: UserLoginRequest): Promise<AuthResponse> => {
        dispatch({ type: 'LOGIN_START' });

        try {
            const response = await loginMutation.mutateAsync(credentials);

            adoptSession(response);

            return response;
        } catch (error: any) {
            const tenants = tenantsFromSelectionError(error);
            if (tenants) {
                dispatch({ type: 'LOGIN_TENANT_REQUIRED' });
                throw new TenantSelectionRequiredError(tenants);
            }

            // Left untranslated on purpose, and left possibly-empty on purpose. This
            // provider sits above the i18n-aware tree that `test-utils` mounts, so giving
            // it a `useTranslation` would change what forty test files render; LoginForm
            // supplies the translated fallback when the server sent no words of its own.
            dispatch({
                type: 'LOGIN_FAILURE',
                payload: parseApiError(error).message,
            });

            throw error;
        }
    };

    const register = async (userData: UserRegistrationRequest) => {
        return registerMutation.mutateAsync(userData);
    };

    const logout = () => {
        // Signing out of an impersonation signs the administrator out too; it is their browser.
        if (isImpersonating()) {
            void endImpersonation();
        }

        // First, while there is still a token to say who is asking: this browser stops
        // being sent push notifications when its owner signs out of it.
        signOutOfPush(localStorage.getItem('authToken'));

        services.authService.removeAuthToken();
        forgetSession();
        dispatch({ type: 'LOGOUT' });
    };

    /**
     * An optimistic local edit, for the callers that have just told the server the same thing
     * (the language picker, the profile page). The next focus asks the server again, and by
     * then it has the new value too.
     */
    const updateUser = (userData: Partial<User>) => {
        const current = latest.current;

        if (!current.user) {
            return;
        }

        const updatedUser: User = { ...current.user, ...userData };
        noteLocalEdit();
        latest.current = { ...current, user: updatedUser };

        sessionStore().setItem('user', JSON.stringify(updatedUser));
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    };

    const value: AuthContextType = {
        ...state,
        login,
        adoptSession,
        register,
        logout,
        updateUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
